import { useIntl } from 'react-intl';
import DefineSpaceIcon from '@ingka/ssr-icon/paths/define-space';
import CheckmarkIcon from '@ingka/ssr-icon/paths/checkmark';
import Button from 'ui/Button/Button';
import { useCore } from 'contexts/core';
import { useAppDispatch } from 'ducks/hooks';
import { setPresentRoomSize } from 'ducks/roomSizeHistory';
import {
  hideMeasurements,
  isMeasurementsVisible,
  isRoomConfigurationActive,
  setInvalidFramePlacement,
  setRoomConfigurationActive,
  showMeasurements
} from 'ducks/layout';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import {
  HelpLinesComponentEditMode,
  HelpLinesComponentVisible,
  InvalidPlacementComponent
} from '@inter-ikea-storageone/range';
import RoomChangeCompleteComponent from '@inter-ikea-storageone/range/src/core-api/components/RoomChangeCompleteComponent';
import { selectGroup } from 'ducks/products';
import { equals } from 'ramda';
import usePrevious from 'hooks/usePrevious';

const RoomConfig = () => {
  const coreApi = useCore();
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const roomConfigurationActive = useSelector(isRoomConfigurationActive);
  const measurementsActive = useSelector(isMeasurementsVisible);

  const [prevMeasurementsActive, setPrevMeasurementsActive] = useState(measurementsActive);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [helpLinesVisibleBeforeRoomConfig, setHelpLinesVisibleBeforeRoomConfig] = useState(false);

  const prevRoomSize = usePrevious(coreApi.kvadratRoom?.getRoomSize());

  useEffect(() => {
    setPrevMeasurementsActive(measurementsActive);
  }, [roomConfigurationActive]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (roomConfigurationActive && measurementsActive) {
      dispatch(hideMeasurements());
    }
  }, [dispatch, roomConfigurationActive, measurementsActive]);

  const onRoomConfigClicked = () => {
    setIsButtonDisabled(true);
    if (!coreApi.kvadratRoom) return;
    const initialDimensions = coreApi.kvadratRoom.getDimensions();

    if (initialDimensions) {
      dispatch(setPresentRoomSize(initialDimensions));
    }

    const helpLinesVisible =
      coreApi.entities
        .getScene()
        ?.getComponent<HelpLinesComponentVisible>(HelpLinesComponentVisible.id)?.visible || false;
    setHelpLinesVisibleBeforeRoomConfig(helpLinesVisible);
    dispatch(setRoomConfigurationActive(true));
    const scene = coreApi.entities.getScene();
    scene?.addComponent(new HelpLinesComponentEditMode(false));
    scene?.addComponent(new HelpLinesComponentVisible(false));

    dispatch(selectGroup(''));

    setTimeout(() => {
      setIsButtonDisabled(false);
    }, 850);
  };

  const onClose = async () => {
    setIsButtonDisabled(true);
    await coreApi.kvadratRoom?.kvadrat?.close();

    const newRoomSize = coreApi.kvadratRoom?.getRoomSize();

    if (newRoomSize && prevRoomSize && !equals(prevRoomSize, newRoomSize)) {
      coreApi.camera.centerRoom();
    }

    const centerPositionOfAllRoomPartsInScene = coreApi.kvadratRoom?.getRoomCenterPosition();
    if (centerPositionOfAllRoomPartsInScene) {
      coreApi.lights?.centerLights(centerPositionOfAllRoomPartsInScene);
    }

    dispatch(setRoomConfigurationActive(false));
    if (prevMeasurementsActive) {
      dispatch(showMeasurements());
    }
    const invalidPlacedFrames = coreApi.entities
      .getByComponent(InvalidPlacementComponent.id)
      .map(entity => entity.id);
    if (invalidPlacedFrames.length > 0) {
      dispatch(setInvalidFramePlacement(true));
    }

    if (helpLinesVisibleBeforeRoomConfig) {
      coreApi.entities.getScene()?.addComponent(new RoomChangeCompleteComponent());
      const scene = coreApi.entities.getScene();
      scene?.addComponent(new HelpLinesComponentVisible(true));
    }

    setIsButtonDisabled(false);

    coreApi.history.sendHistoryEvent();
  };

  return (
    <>
      {!roomConfigurationActive ? (
        <Button
          disabled={isButtonDisabled}
          onClick={onRoomConfigClicked}
          size={'small'}
          text={formatMessage({ id: 'storageone.customiseRoom' })}
          ssrIcon={DefineSpaceIcon}
          type="primary"
        />
      ) : (
        <Button
          disabled={isButtonDisabled}
          onClick={onClose}
          size={'small'}
          text={formatMessage({ id: 'storageone.done' })}
          ssrIcon={CheckmarkIcon}
          type="primary"
        />
      )}
    </>
  );
};

export default RoomConfig;
